import React from 'react';
import { Card, Input, Typography, Space, Select, Button } from 'antd';
import { QuizOption } from './QuizAtoms';
import { splitSentenceAtWord } from '../../utils/spellingGap';
import { playHover, playSelect } from '../../utils/sound';

const { Text } = Typography;

/**
 * Question-type renderer registry (ROO-180 phase 1).
 *
 * Every answer-input UI lives HERE, once. TestTaking dispatches through
 * renderQuestionInput(); PlacementQuiz shares the choice/extract atoms. Adding
 * a question type = adding one renderer + one grading branch server-side.
 *
 * Contract: value is the string stored in the answers map (JSON-encoded for
 * structured types — the same wire format the submit endpoints parse).
 */

export interface QuizQuestionLike {
  id: string;
  type: string;
  subject?: string;
  content?: any;
  source_metadata?: any;
  answers?: Array<{ text: string; value: string; img?: string | null }>;
}

export type AnswerChange = (value: string) => void;

// ── type detection (moved from TestTaking, ROO-180) ─────────────────────────
export const isEssayQuestion = (q?: QuizQuestionLike): boolean => {
  if (!q) return false;
  if ((q.subject || '').toLowerCase() === 'writing') return true;
  if (q.content?.responseFormat === 'essay') return true;
  if (q.source_metadata?.naplan?.requiresManualRubric === true) return true;
  return false;
};
export const isTextInputQuestion = (q?: QuizQuestionLike): boolean =>
  !!q && q.content?.responseFormat === 'text';
export const isNumericQuestion = (q?: QuizQuestionLike): boolean =>
  !!q && q.content?.responseFormat === 'numeric';

export const countWords = (text: string): number => {
  const t = (text || '').trim();
  return t ? t.split(/\s+/).length : 0;
};

// ── shared blocks ────────────────────────────────────────────────────────────

/** Multi-extract reading comparison: stacked extract cards (ROO-173). */
export const ExtractsBlock: React.FC<{ extracts?: any[] }> = ({ extracts }) => {
  if (!Array.isArray(extracts) || extracts.length === 0) return null;
  return (
    <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} data-testid="extracts">
      {extracts.map((ex: any, i: number) => (
        <Card key={i} size="small" style={{ background: '#FBF9F2', border: '1.5px solid #EFE6D0', borderRadius: 12 }}>
          <Text strong style={{ color: '#14593C' }}>
            {ex.label || `Extract ${String.fromCharCode(65 + i)}`}{ex.title ? ` — ${ex.title}` : ''}
          </Text>
          <div style={{ whiteSpace: 'pre-line', fontSize: 15, lineHeight: 1.7, color: '#374151', marginTop: 6 }}>
            {ex.content}
          </div>
        </Card>
      ))}
    </Space>
  );
};

// ── input renderers ──────────────────────────────────────────────────────────

const EssayInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  const c = q.content || {};
  const promptText = c.questionText || c.question || c.fullPromptText || '';
  return (
    <>
      {promptText && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <div style={{ whiteSpace: 'pre-line', fontSize: 15, lineHeight: 1.7, color: '#374151' }}>{promptText}</div>
        </Card>
      )}
      <Input.TextArea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={playSelect}
        placeholder="Write your response here..."
        autoSize={{ minRows: 12, maxRows: 30 }}
        style={{ fontSize: 16, lineHeight: 1.7 }}
      />
      <Text type="secondary" style={{ display: 'block', textAlign: 'right' }}>
        {countWords(value)} words
      </Text>
    </>
  );
};

const TextResponseInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  const c = q.content || {};
  const sentence = c.questionText || c.question || '';
  const misspelled = (c.misspelledWord || '').toString();
  const split = misspelled ? splitSentenceAtWord(sentence, misspelled) : null;

  if (split) {
    // Spelling with a known misspelled word (ROO-212): call out the word to
    // correct, then show the sentence with an inline gap input in its place.
    const gapWidth = Math.max(misspelled.length + 4, 10);
    return (
      <>
        <Text type="secondary">
          The word{' '}
          <Text strong delete style={{ color: '#cf1322' }}>
            {misspelled}
          </Text>{' '}
          is spelled incorrectly. Type the correct spelling in the gap.
        </Text>
        <Card size="small" style={{ background: '#fafafa' }}>
          <div style={{ fontSize: 17, lineHeight: 2.4, color: '#374151' }}>
            {split.before}
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={playSelect}
              placeholder="…"
              size="large"
              aria-label="Type the correct spelling"
              style={{
                display: 'inline-block',
                width: `${gapWidth}ch`,
                margin: '0 4px',
                fontSize: 16,
                textAlign: 'center',
                verticalAlign: 'baseline',
              }}
            />
            {split.after}
          </div>
        </Card>
      </>
    );
  }

  // Fallback (no misspelledWord on the item): sentence + separate input.
  return (
    <>
      {sentence && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <div style={{ fontSize: 16, lineHeight: 1.7, color: '#374151' }}>{sentence}</div>
        </Card>
      )}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={playSelect}
        placeholder="Type the correct spelling..."
        size="large"
        style={{ fontSize: 16 }}
      />
    </>
  );
};

const NumericInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  const c = q.content || {};
  const prompt = c.questionText || c.question || '';
  return (
    <>
      {prompt && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <div style={{ whiteSpace: 'pre-line', fontSize: 16, lineHeight: 1.7, color: '#374151' }}>{prompt}</div>
        </Card>
      )}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={playSelect}
        placeholder="Enter your answer..."
        inputMode="decimal"
        size="large"
        style={{ fontSize: 16, maxWidth: 280 }}
      />
    </>
  );
};

/** Tap-to-place sequencing (ROO-173): value is a JSON array of item ids. */
const DragAndDropInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  const items: any[] = q.content?.items || [];
  let placed: string[] = [];
  try { placed = JSON.parse(value || '[]'); } catch { /* fresh */ }
  if (!Array.isArray(placed)) placed = [];
  const pool = items.filter((it) => !placed.includes(it.id));
  const commit = (arr: string[]) => onChange(JSON.stringify(arr));
  const textOf = (qid: string) => items.find((it) => it.id === qid)?.text || qid;
  return (
    <>
      <Text type="secondary">Tap the items below in the correct order — tap a placed item to remove it.</Text>
      <div data-testid="dnd-sequence" style={{ border: '1.5px dashed #d9d9d9', borderRadius: 10, padding: 12, minHeight: 56, margin: '10px 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {placed.length === 0 && <Text type="secondary">Your order appears here…</Text>}
        {placed.map((qid, i) => (
          <Button
            key={qid}
            onMouseEnter={playHover}
            onClick={() => { playSelect(); commit(placed.filter((x) => x !== qid)); }}
          >
            {i + 1}. {textOf(qid)} ✕
          </Button>
        ))}
      </div>
      <div data-testid="dnd-pool" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {pool.map((it) => (
          <Button
            key={it.id}
            type="dashed"
            onMouseEnter={playHover}
            onClick={() => { playSelect(); commit([...placed, it.id]); }}
          >
            {it.text}
          </Button>
        ))}
      </div>
    </>
  );
};

/** Dropdown cloze (ROO-173): value is a JSON {blankId: optionId} map. */
const DropdownClozeInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  const c: any = q.content || {};
  const text: string = c.text || c.question || c.questionText || '';
  const blanks: any[] = Array.isArray(c.blanks) ? c.blanks : [];
  let chosen: Record<string, string> = {};
  try { chosen = JSON.parse(value || '{}'); } catch { /* fresh */ }
  if (!chosen || typeof chosen !== 'object') chosen = {};
  const setBlank = (b: string, v: string) => onChange(JSON.stringify({ ...chosen, [b]: v }));
  const parts = text.split(/(\{\{\w+\}\})/g);
  return (
    <div style={{ fontSize: 16, lineHeight: 2.4 }} data-testid="cloze-text">
      {parts.map((p, i) => {
        const m = p.match(/^\{\{(\w+)\}\}$/);
        if (!m) return <span key={i}>{p}</span>;
        const blank = blanks.find((b) => String(b.id) === m[1]);
        return (
          <Select
            key={i}
            size="middle"
            style={{ minWidth: 140, margin: '0 4px' }}
            placeholder="Choose…"
            value={chosen[m[1]]}
            onFocus={playSelect}
            onChange={(v) => setBlank(m[1], v)}
            options={(blank?.options || []).map((o: any) => ({ value: o.id, label: o.text }))}
          />
        );
      })}
    </div>
  );
};

const ChoiceInput: React.FC<{ q: QuizQuestionLike; value: string; onChange: AnswerChange }> = ({ q, value, onChange }) => {
  // multiple_choice = several options may be correct: toggle selections and
  // store them as a JSON-encoded array (the format test grading parses,
  // ROO-185). single_choice keeps the plain option-id string.
  const isMulti = q.type === 'multiple_choice';
  let picked: string[] = [];
  if (isMulti) {
    try { picked = JSON.parse(value || '[]'); } catch { picked = value ? [value] : []; }
    if (!Array.isArray(picked)) picked = [];
  }
  const toggle = (v: string) => {
    if (!isMulti) return onChange(v);
    const next = picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v];
    // Empty selection clears the answer so palette/answered counts stay honest.
    onChange(next.length ? JSON.stringify(next) : '');
  };
  const isSelected = (v: string) => (isMulti ? picked.includes(v) : value === v);
  return (
    <>
      {q.content?.questionText && (
        <div style={{ marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: q.content.questionText }} />
      )}
      {isMulti && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Select all that apply</Text>
      )}
      <div className="pq-options" role={isMulti ? 'group' : 'radiogroup'} aria-label="Answer options">
        {(q.answers || []).map((answer, index) => (
          <QuizOption
            key={index}
            selected={isSelected(answer.value)}
            multi={isMulti}
            onClick={() => toggle(answer.value)}
            text={answer.text}
            img={answer.img}
            index={index + 1}
          />
        ))}
      </div>
    </>
  );
};

// ── dispatch ─────────────────────────────────────────────────────────────────

/** One entry point: pick the input renderer for a question. */
export function renderQuestionInput(
  q: QuizQuestionLike,
  value: string,
  onChange: AnswerChange,
): React.ReactNode {
  if (isEssayQuestion(q)) return <EssayInput q={q} value={value} onChange={onChange} />;
  if (isTextInputQuestion(q)) return <TextResponseInput q={q} value={value} onChange={onChange} />;
  if (isNumericQuestion(q)) return <NumericInput q={q} value={value} onChange={onChange} />;
  if (q.type === 'drag_and_drop') return <DragAndDropInput q={q} value={value} onChange={onChange} />;
  if (q.type === 'dropdown_cloze') return <DropdownClozeInput q={q} value={value} onChange={onChange} />;
  return <ChoiceInput q={q} value={value} onChange={onChange} />;
}
